import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

import { cn } from '@ui/lib/utils';

export const MobileLayout = ({
  children,
  className,
  title,
  backHref,
  onBack,
  backLabel = 'Back',
  rightButton,
  transparent = false,
  noPadding = false,
  noScroll = false
}: {
  children: React.ReactNode;
  className?: string;
  title?: React.ReactNode;
  backHref?: string;
  onBack?: () => void;
  /**
   * `aria-label` for the back button. Defaults to the English literal "Back"
   * because this component lives in `@ui` and can't read translations itself.
   * Consumers in localised contexts SHOULD pass `t('Common.goBack')` (or
   * equivalent) so screen-reader users in non-English locales hear the
   * translated label.
   */
  backLabel?: string;
  rightButton?: React.ReactNode;
  transparent?: boolean;
  noPadding?: boolean;
  /** Disable internal overflow-y-auto so children can manage their own scrolling. */
  noScroll?: boolean;
}) => {
  const handleBackClick = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className={cn('flex flex-col flex-1 min-h-0 overflow-x-hidden', className)}>
      {/* Mobile Header */}
      {(title || backHref || onBack || rightButton) && (
        <div className="md:hidden">
          {transparent ? (
            // Avoid horizontal overflow: fixed full-width wrapper, layout-bounds inside.
            <div className="fixed top-0 left-0 right-0 z-50 overflow-x-hidden">
              <div className="layout-bounds">
                <div className="flex items-center gap-2 p-3">
                  {(backHref || onBack) && (
                    <>
                      {backHref ? (
                        <Link
                          href={backHref}
                          aria-label={backLabel}
                          className={cn(
                            'text-sm text-neutral-900 cursor-pointer',
                            transparent && 'rounded-full bg-white border border-neutral-200'
                          )}
                        >
                          <ArrowLeft className="size-10 p-2" aria-hidden="true" />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={handleBackClick}
                          aria-label={backLabel}
                          className={cn(
                            'text-sm text-neutral-900 cursor-pointer',
                            transparent && 'rounded-full bg-white'
                          )}
                        >
                          <ArrowLeft className="size-10 p-2" aria-hidden="true" />
                        </button>
                      )}
                    </>
                  )}
                  {title && <h1 className="text-base font-medium px-2 py-2">{title}</h1>}
                  {rightButton && <div className="flex-1 flex justify-end">{rightButton}</div>}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 border-b border-neutral-200">
              {(backHref || onBack) && (
                <>
                  {backHref ? (
                    <Link href={backHref} aria-label={backLabel} className="text-sm text-neutral-900 cursor-pointer">
                      <ArrowLeft className="size-10 p-2" aria-hidden="true" />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={handleBackClick}
                      aria-label={backLabel}
                      className="text-sm text-neutral-900 cursor-pointer"
                    >
                      <ArrowLeft className="size-10 p-2" aria-hidden="true" />
                    </button>
                  )}
                </>
              )}
              {title && <h1 className="text-base font-medium px-2 py-2">{title}</h1>}
              {rightButton && <div className="flex-1 flex justify-end">{rightButton}</div>}
            </div>
          )}
        </div>
      )}

      <div
        className={cn(
          'flex-1 min-h-0 flex flex-col',
          !noScroll && 'overflow-y-auto md:overflow-visible',
          'overflow-x-hidden',
          !noPadding && 'px-5 py-2 md:px-0 md:py-0'
        )}
      >
        {children}
      </div>
    </div>
  );
};
